import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cargoAPI, jetAPI } from "@/lib/api";
import { toast } from "sonner";
import { 
  Package, 
  Plus,
  Truck, 
  Train, 
  Plane, 
  Ship,
  Loader2,
  Trash2,
  ArrowRight
} from "lucide-react";

const transportIcons = {
  road: Truck,
  rail: Train,
  air: Plane,
  water: Ship,
};

export default function MyListings() {
  const [cargoListings, setCargoListings] = useState([]);
  const [jetListings, setJetListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("cargo");

  const fetchListings = useCallback(async () => {
    try {
      const [cargoRes, jetRes] = await Promise.all([
        cargoAPI.getMine(),
        jetAPI.getMine()
      ]);
      setCargoListings(cargoRes.data);
      setJetListings(jetRes.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to fetch listings:', error.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchListings();
  }, [fetchListings]);

  const handleDeleteCargo = async (id) => {
    if (!window.confirm("Delete this listing?")) return;
    try {
      await cargoAPI.delete(id);
      setCargoListings(cargoListings.filter(l => l.id !== id));
      toast.success("Listing deleted");
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const handleDeleteJet = async (id) => {
    if (!window.confirm("Delete this listing?")) return;
    try {
      await jetAPI.delete(id);
      setJetListings(jetListings.filter(l => l.id !== id));
      toast.success("Listing deleted");
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-black tracking-tight">My Listings</h1>
        <Link to="/partner/add">
          <Button className="rounded-none btn-hover" data-testid="add-new-listing-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add New
          </Button>
        </Link>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="rounded-none">
          <TabsTrigger value="cargo" className="rounded-none" data-testid="cargo-tab">
            Cargo Space ({cargoListings.length})
          </TabsTrigger>
          <TabsTrigger value="jets" className="rounded-none" data-testid="jets-tab">
            Private Jets ({jetListings.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="cargo" className="mt-6">
          {cargoListings.length === 0 ? (
            <div className="text-center py-10 border border-border">
              <Package className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
              <p className="text-muted-foreground">No cargo listings yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {cargoListings.map((listing) => {
                const Icon = transportIcons[listing.transport_mode] || Package;
                return (
                  <div key={listing.id} className="border border-border p-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-secondary flex items-center justify-center">
                        <Icon className="w-6 h-6" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 font-bold">
                          <span>{listing.origin_city}</span>
                          <ArrowRight className="w-4 h-4 text-muted-foreground" />
                          <span>{listing.destination_city}</span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {listing.departure_date} • {listing.available_capacity} kg • ₹{listing.price_per_kg}/kg
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 text-xs font-bold uppercase ${listing.status === 'active' ? 'status-active' : 'status-cancelled'}`}>
                        {listing.status}
                      </span>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDeleteCargo(listing.id)}
                        data-testid={`delete-cargo-${listing.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="jets" className="mt-6">
          {jetListings.length === 0 ? (
            <div className="text-center py-10 border border-border">
              <Plane className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
              <p className="text-muted-foreground">No jet listings yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {jetListings.map((listing) => (
                <div key={listing.id} className="border border-border p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-amber-500/10 flex items-center justify-center">
                      <Plane className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 font-bold">
                        <span>{listing.origin_city}</span>
                        <ArrowRight className="w-4 h-4 text-muted-foreground" />
                        <span>{listing.destination_city}</span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {listing.jet_model} • {listing.departure_date} • {listing.available_seats} seats
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 text-xs font-bold uppercase ${listing.status === 'active' ? 'status-active' : 'status-cancelled'}`}>
                      {listing.status}
                    </span>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleDeleteJet(listing.id)}
                      data-testid={`delete-jet-${listing.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
